import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button, Divider, SummaryInformationCategory, TItem, DateTools, IconButton } from '@treezor/design-system';

import { useUserActions } from '@/features/user/hooks/useUserActions';
import { physicalUserRole, selfEmployedLegalForms, TLegalFormKey, UserTypes } from '@/features/user/store/user.model';
import { useUserInfo } from '@/features/user/hooks/useUserInfo';
import { useProfileContext } from '@/features/context/ProfileContext';
import { useAccesControl } from '@/hooks/useAccessControl';
import { useAppDispatch } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { AddressSummary } from '@/features/user/components/CreateUser/AddressSummary';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { openModal } from '@/store/actions/core.actions';
import { useKycStatus } from '@/features/kyc/hooks/useKycStatus';

import { IMainInformationEditionModalProps } from './MainInformationEditionModal';

export function MainInformation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = useDashboardConfig();
  const dispatch = useAppDispatch();

  const { user } = useProfileContext();
  const { navigateToUser } = useUserActions();
  const { userRole, userType } = useUserInfo(user);
  const { hasAccessToFeature } = useAccesControl();
  const { kycStatus } = useKycStatus(user);

  const isResctrited = !!config?.settings?.users?.actions
    ? config.settings.users.actions.includes('restrictUserInfo')
    : false;

  const hasAccessToKycKybTab = useMemo(
    () => kycStatus !== 'not_required' && config?.settings.users?.tabs?.includes('kyckyb'),
    [config?.settings.users?.tabs, kycStatus],
  );

  const onEditMainInfo = useCallback(() => {
    dispatch(openModal<IMainInformationEditionModalProps>('mainInformationEditionModal', { user }, { maxWidth: 'md' }));
  }, [dispatch, user]);

  const firstLeftPartItems: TItem[] = useMemo(
    () => [
      ...(user.userTypeId === UserTypes.naturalPerson && userRole !== physicalUserRole.anonymous
        ? [
            {
              label: t('pages.currentProfiles.profile.informations.title'),
              value: user.title ? t(`pages.dashboard.users.titles.${user.title}`) : '/',
            },
          ]
        : []),
      ...(user.userTypeId === UserTypes.naturalPerson && userRole !== physicalUserRole.anonymous
        ? [
            { label: t('pages.currentProfiles.profile.informations.lastname'), value: user.lastname || '/' },
            {
              label: t('pages.currentProfiles.profile.informations.firstname'),
              value: [user.firstname, ...user.middleNames.split(', ')].filter(Boolean).join(', ') || '/',
            },
          ]
        : []),
      ...(user.userTypeId !== UserTypes.naturalPerson ||
      selfEmployedLegalForms.includes(Number(user?.legalForm) as TLegalFormKey)
        ? [
            { label: t('pages.currentProfiles.profile.informations.legalName'), value: user.legalName || '/' },
            {
              label: t('pages.currentProfiles.profile.informations.legalRegistrationNumber'),
              value: user.legalRegistrationNumber,
            },
          ]
        : []),
      { label: t('pages.currentProfiles.profile.contact.email'), value: user.email },
    ],
    [
      t,
      user.email,
      user.firstname,
      user.lastname,
      user.legalForm,
      user.legalName,
      user.legalRegistrationNumber,
      user.middleNames,
      user.title,
      user.userTypeId,
      userRole,
    ],
  );

  const secondLeftPartItems: TItem[] = useMemo(
    () => [
      {
        label: t('pages.currentProfiles.profile.informations.createdAt'),
        value: DateTools.format(user.createdDate, 'date-time') || '/',
      },
      {
        label: t('pages.currentProfiles.profile.informations.updatedAt'),
        value: DateTools.format(user.modifiedDate, 'date-time') || '/',
      },
    ],
    [t, user.createdDate, user.modifiedDate],
  );

  const firstMiddlePartItems: TItem[] = useMemo(
    () => [
      ...(user.userTypeId === UserTypes.naturalPerson && userRole !== physicalUserRole.anonymous
        ? [
            {
              label: t('pages.currentProfiles.profile.informations.birthday'),
              value: user.birthday ? DateTools.format(user.birthday) || '/' : '/',
            },
          ]
        : []),
      { label: t('pages.currentProfiles.profile.informations.language'), value: user.language?.toUpperCase() || '/' },
    ],
    [t, user.birthday, user.language, user.userTypeId, userRole],
  );

  const secondMiddlePartItems = useMemo(
    () => [
      {
        label: t('pages.currentProfiles.profile.informations.userType'),
        value:
          user.userTypeId === UserTypes.naturalPerson && userRole
            ? t(`pages.dashboard.users.physicalUserRole.${userRole}`)
            : userType,
      },
      ...(user.parentUserId
        ? [
            {
              label: t('pages.currentProfiles.profile.informations.parent'),
              value: (
                <div className="flex items-center justify-center ">
                  #{user.parentUserId}
                  <IconButton
                    icon="chevron_right"
                    onClick={() => navigateToUser(user.parentUserId)}
                    className="m-0"
                    variant="tertiary"
                    size="s"
                  />
                </div>
              ),
            },
          ]
        : []),
      config?.settings.users?.distributionCountry?.length
        ? {
            label: t('pages.currentProfiles.profile.informations.distributionCountry'),
            value: user.distributionCountry ? t(`shared.countries.${user.distributionCountry}`) : '/',
          }
        : [],
    ],
    [
      config?.settings.users?.distributionCountry?.length,
      navigateToUser,
      t,
      user.distributionCountry,
      user.parentUserId,
      user.userTypeId,
      userRole,
      userType,
    ],
  ) as TItem[];

  const RightPartItems: TItem[] = useMemo(
    () => [
      {
        label: t('pages.currentProfiles.profile.contact.phone'),
        value: user.phone || '/',
      },
      {
        label: t('pages.currentProfiles.profile.contact.mobile'),
        value: user.mobile || '/',
      },
    ],
    [t, user.mobile, user.phone],
  );

  return (
    <div className="rounded-200 border-secondary border-[1px] p-6 gap-5 flex flex-col bg-gradient-to-r from-blue-400/10 to-green-200/10">
      <div className="flex">
        <h6 className="typo-h6-bold">{t('pages.dashboard.createUser.form.blocks.mainInformation')}</h6>
        {hasAccessToKycKybTab && (
          <Button
            variant="ghost"
            size="s"
            className="ml-auto"
            icon="chevron_right"
            iconPosition="right"
            onClick={() => navigate(DashboardHelpers.generateProfilePath(user.userId, { kycKyb: {} }))}
          >
            {t('pages.currentProfiles.profile.informations.seeDetails')}
          </Button>
        )}
        {!hasAccessToKycKybTab &&
          hasAccessToFeature(featuresSlugs.EDIT_INFORMATIONS) &&
          (!isResctrited || !!user.parentUserId) && (
            <IconButton size="m" icon="edit" className="ml-auto" onClick={onEditMainInfo} />
          )}
      </div>
      <div className="flex flex-row gap-7">
        <div className="flex flex-col gap-4">
          <SummaryInformationCategory items={firstLeftPartItems} />
          <Divider direction="horizontal" thickness="100" variant="secondary" />
          <SummaryInformationCategory items={secondLeftPartItems} />
        </div>
        <Divider thickness="100" direction="vertical" variant="secondary" length="auto" />
        <div className="flex flex-col gap-4">
          <SummaryInformationCategory items={firstMiddlePartItems} />
          <Divider direction="horizontal" thickness="100" variant="secondary" />
          <SummaryInformationCategory items={secondMiddlePartItems} />
        </div>
        <Divider thickness="100" direction="vertical" variant="secondary" length="auto" />
        <div className="flex flex-col gap-4">
          <SummaryInformationCategory items={RightPartItems} />
          <Divider direction="horizontal" thickness="100" variant="secondary" />
          <AddressSummary
            isPrimary
            title={t('pages.dashboard.createUser.form.mainAddress')}
            address1={user.address1}
            address2={user.address2}
            address3={user.address3}
            postcode={user.postcode}
            city={user.city}
            country={user.country}
          />
        </div>
      </div>
    </div>
  );
}
